<template>
  <div class="dashboard-container">
    <div v-if="loading" class="loading-state">
      <svg class="spinner" viewBox="0 0 50 50">
        <circle
          class="path"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke-width="4"
        ></circle>
      </svg>
      <p>Loading your dashboard...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <div class="error-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <p>{{ error }}</p>
    </div>

    <div v-else class="dashboard-content">
      <!-- Tab Navigation -->
      <div class="tab-navigation">
        <div class="tab-list" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="['tab-button', { active: activeTab === tab.id }]"
            :aria-selected="activeTab === tab.id"
            role="tab"
            @click="activeTab = tab.id"
          >
            <component :is="tab.icon" class="tab-icon" />
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Email Accounts Tab -->
        <div
          v-if="activeTab === 'emails'"
          class="tab-panel"
          role="tabpanel"
          aria-labelledby="emails-tab"
        >
          <div class="email-accounts-section">
            <!-- Search bar -->
            <div class="search-bar">
              <div class="search-input-wrapper">
                <svg
                  class="search-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Search email addresses..."
                  class="search-input"
                  @keyup.enter="performSearch"
                />
                <button
                  @click="clearSearch"
                  v-if="searchQuery"
                  class="clear-button"
                  aria-label="Clear search"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <button
                @click="performSearch"
                class="search-button"
                :disabled="loading"
              >
                <svg
                  v-if="loading"
                  class="button-spinner"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle cx="12" cy="12" r="10" />
                </svg>
                <span v-else>Search</span>
              </button>
            </div>

            <!-- Email List Container -->
            <div class="email-list-container" v-if="filteredEmails.length > 0">
              <!-- Header -->
              <div class="list-header">
                <div class="header-row">
                  <div class="column-header email">Email</div>
                  <div class="column-header status">Status</div>
                  <div class="column-header date">Created</div>
                  <div class="column-header actions">Actions</div>
                </div>
              </div>

              <!-- Email list -->
              <div class="list-body">
                <div
                  v-for="email in paginatedEmails"
                  :key="email.id"
                  class="email-row"
                  @click="goToInbox(email.id)"
                >
                  <!-- Email column -->
                  <div class="column-content email">
                    <div class="email-info">
                      <div class="email-address">
                        <div
                          class="email-avatar"
                          :style="{
                            backgroundColor: getEmailColor(email.email),
                          }"
                        >
                          {{ getEmailInitials(email.email) }}
                        </div>
                        <div class="email-details">
                          <div class="email-text">{{ email.email }}</div>
                          <div
                            class="email-meta"
                            v-if="email.first_name || email.last_name"
                          >
                            {{ email.first_name }} {{ email.last_name }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Status column -->
                  <div class="column-content status">
                    <span
                      class="status-indicator"
                      :class="
                        email.active ? 'status-active' : 'status-inactive'
                      "
                    >
                      <span class="status-dot"></span>
                      {{ email.active ? "Active" : "Inactive" }}
                    </span>
                  </div>

                  <!-- Date column -->
                  <div class="column-content date">
                    <div class="date-info">
                      <div class="date-text">
                        {{ formatDate(email.created_at) }}
                      </div>
                    </div>
                  </div>

                  <!-- Actions column -->
                  <div class="column-content actions">
                    <div class="action-buttons">
                      <button
                        @click.stop="deleteEmail(email)"
                        class="action-button delete-button"
                        title="Delete email"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path
                            d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div
              v-if="!loading && filteredEmails.length === 0"
              class="empty-state"
            >
              <div class="empty-state-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                  <path d="M22 7l-10 7L2 7"></path>
                </svg>
              </div>
              <h2 class="empty-state-title">
                {{ searchQuery ? "No results found" : "No email accounts" }}
              </h2>
              <p v-if="searchQuery" class="empty-state-message">
                No email accounts found matching "{{ searchQuery }}".
              </p>
              <p v-else class="empty-state-message">
                You don't have any email accounts set up yet.
              </p>
            </div>

            <!-- Pagination -->
            <div class="pagination-container" v-if="filteredEmails.length > 0">
              <div class="pagination-info">
                Showing <span class="highlight">{{ startIndex + 1 }}</span> to
                <span class="highlight">{{ endIndex }}</span> of
                <span class="highlight">{{ totalEmails }}</span> email accounts
              </div>

              <!-- Desktop pagination -->
              <div class="pagination-controls">
                <button
                  @click="prevPage"
                  :disabled="currentPage === 1"
                  class="pagination-arrow"
                  aria-label="Previous page"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>

                <div class="pagination-pages">
                  <button
                    v-for="page in displayedPages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="[
                      'pagination-page',
                      currentPage === page ? 'active' : '',
                      typeof page !== 'number' ? 'ellipsis' : '',
                    ]"
                  >
                    {{ page }}
                  </button>
                </div>

                <button
                  @click="nextPage"
                  :disabled="currentPage === totalPages"
                  class="pagination-arrow"
                  aria-label="Next page"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>

              <!-- Mobile pagination -->
              <div class="mobile-pagination">
                <button
                  @click="prevPage"
                  :disabled="currentPage === 1"
                  class="mobile-pagination-button"
                >
                  Previous
                </button>
                <span class="mobile-pagination-info">
                  Page {{ currentPage }} of {{ totalPages }}
                </span>
                <button
                  @click="nextPage"
                  :disabled="currentPage === totalPages"
                  class="mobile-pagination-button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Conversations Tab -->
        <div
          v-if="activeTab === 'conversations'"
          class="tab-panel"
          role="tabpanel"
          aria-labelledby="conversations-tab"
        >
          <div class="conversations-section">
            <!-- Enhanced Search bar -->
            <div class="search-section">
              <div class="search-bar">
                <div class="search-input-wrapper">
                  <svg
                    class="search-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    type="text"
                    v-model="conversationSearchQuery"
                    placeholder="Search conversations, subjects, content, participants..."
                    class="search-input"
                    @keyup.enter="performConversationSearch"
                    @input="handleSearchInput"
                  />
                  <button
                    @click="clearConversationSearch"
                    v-if="conversationSearchQuery"
                    class="clear-button"
                    aria-label="Clear search"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <button
                  @click="performConversationSearch"
                  class="search-button"
                  :disabled="conversationsLoading"
                >
                  <svg
                    v-if="conversationsLoading"
                    class="button-spinner"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                  <span v-else>Search</span>
                </button>
              </div>

              <!-- Search tips -->
              <div class="search-tips" v-if="showSearchTips">
                <p class="tips-title">💡 Search Tips:</p>
                <ul class="tips-list">
                  <li>
                    Search by <strong>subject</strong>,
                    <strong>content</strong>, or
                    <strong>participant email</strong>
                  </li>
                  <li>
                    Use partial words - try "john" to find "john@example.com"
                  </li>
                  <li>Search across all your conversations and emails</li>
                  <li>Results are sorted by most recent activity</li>
                </ul>
              </div>

              <!-- Search stats -->
              <div
                v-if="conversationSearchQuery && searchStats"
                class="search-stats"
              >
                <svg
                  class="stats-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <span class="stats-text">
                  Found {{ searchStats.count }} conversation{{
                    searchStats.count !== 1 ? "s" : ""
                  }}
                  {{
                    searchStats.searchTime
                      ? `in ${searchStats.searchTime}ms`
                      : ""
                  }}
                </span>
              </div>
            </div>

            <!-- Conversation List Container -->
            <div
              class="conversation-list-container"
              v-if="filteredMessages.length > 0"
            >
              <ConversationList
                :conversations="conversations"
                :loading="conversationsLoading"
                :totalItems="totalConversationsCount"
                :showRecipient="true"
                @conversation-click="openConversation"
                @delete-conversation="handleDeleteConversation"
                @mark-read="handleMarkConversationAsRead"
                @refresh="fetchConversations"
              />
            </div>

            <!-- Empty state -->
            <div
              v-if="!conversationsLoading && filteredMessages.length === 0"
              class="empty-state"
            >
              <div class="empty-state-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                  ></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <h2 class="empty-state-title">
                {{
                  conversationSearchQuery
                    ? "No conversations found"
                    : "No conversations"
                }}
              </h2>
              <p v-if="conversationSearchQuery" class="empty-state-message">
                No conversations found matching "{{ conversationSearchQuery }}".
              </p>
              <p v-else class="empty-state-message">
                You don't have any conversations yet.
              </p>
            </div>

            <!-- Conversations Pagination -->
            <div
              class="pagination-container"
              v-if="filteredMessages.length > 0"
            >
              <div class="pagination-info">
                Showing
                <span class="highlight">{{ messagesStartIndex + 1 }}</span> to
                <span class="highlight">{{ messagesEndIndex }}</span> of
                <span class="highlight">{{ totalMessages }}</span> conversations
              </div>

              <!-- Desktop pagination -->
              <div class="pagination-controls">
                <button
                  @click="prevConversationPage"
                  :disabled="currentConversationPage === 1"
                  class="pagination-arrow"
                  aria-label="Previous page"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>

                <div class="pagination-pages">
                  <button
                    v-for="page in displayedMessagePages"
                    :key="page"
                    @click="goToConversationPage(page)"
                    :class="[
                      'pagination-page',
                      currentConversationPage === page ? 'active' : '',
                      typeof page !== 'number' ? 'ellipsis' : '',
                    ]"
                  >
                    {{ page }}
                  </button>
                </div>

                <button
                  @click="nextConversationPage"
                  :disabled="currentConversationPage === totalMessagePages"
                  class="pagination-arrow"
                  aria-label="Next page"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>

              <!-- Mobile pagination -->
              <div class="mobile-pagination">
                <button
                  @click="prevConversationPage"
                  :disabled="currentConversationPage === 1"
                  class="mobile-pagination-button"
                >
                  Previous
                </button>
                <span class="mobile-pagination-info">
                  Page {{ currentConversationPage }} of {{ totalMessagePages }}
                </span>
                <button
                  @click="nextConversationPage"
                  :disabled="currentConversationPage === totalMessagePages"
                  class="mobile-pagination-button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, h } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import ConversationList from "@/components/ConversationList.vue";

// Tab icons as inline components - fix template compilation issues
const EmailIcon = {
  render() {
    return h(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        "stroke-width": "2",
      },
      [
        h("rect", { x: "2", y: "4", width: "20", height: "16", rx: "2" }),
        h("path", { d: "M22 7l-10 7L2 7" }),
      ]
    );
  },
};

const MessageIcon = {
  render() {
    return h(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        width: "24",
        height: "24",
        viewBox: "0 0 24 24",
      },
      [
        h("path", {
          fill: "currentColor",
          d: "M16 14h.5c.827 0 1.5-.673 1.5-1.5v-9c0-.827-.673-1.5-1.5-1.5h-13C2.673 2 2 2.673 2 3.5V18l5.333-4zm-9.333-2L4 14V4h12v8z",
        }),
        h("path", {
          fill: "currentColor",
          d: "M20.5 8H20v6.001c0 1.1-.893 1.993-1.99 1.999H8v.5c0 .827.673 1.5 1.5 1.5h7.167L22 22V9.5c0-.827-.673-1.5-1.5-1.5",
        }),
      ]
    );
  },
};

export default {
  name: "MainDashboard",
  components: {
    EmailIcon,
    MessageIcon,
    ConversationList,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const loading = ref(true);
    const error = ref("");
    const emailAddresses = ref([]);
    const currentPage = ref(1);
    const itemsPerPage = ref(100);
    const searchQuery = ref("");
    const totalCount = ref(0);
    const nextPageUrl = ref(null);
    const previousPageUrl = ref(null);

    // Messages state
    const conversations = ref([]);
    const conversationsLoading = ref(false);
    const conversationSearchQuery = ref("");
    const currentConversationPage = ref(1);
    const conversationsPerPage = ref(50);
    const totalConversationsCount = ref(0);
    const nextConversationPageUrl = ref(null);
    const previousConversationPageUrl = ref(null);

    // Enhanced search state
    const showSearchTips = ref(true);
    const searchStats = ref(null);
    const searchDebounceTimer = ref(null);

    // Tab management
    const activeTab = ref("conversations"); // Default to conversations tab
    const tabs = [
      {
        id: "conversations",
        label: "Conversations",
        icon: MessageIcon,
      },
      {
        id: "emails",
        label: "Emails",
        icon: EmailIcon,
      },
    ];

    // Compute filtered emails based on search query
    const filteredEmails = computed(() => {
      return emailAddresses.value;
    });

    // Compute filtered messages based on search query
    const filteredMessages = computed(() => {
      return conversations.value;
    });

    // Pagination computed properties
    const totalEmails = computed(() => totalCount.value);

    const totalPages = computed(() =>
      Math.max(1, Math.ceil(totalEmails.value / itemsPerPage.value))
    );

    const startIndex = computed(
      () => (currentPage.value - 1) * itemsPerPage.value
    );

    const endIndex = computed(() =>
      Math.min(startIndex.value + itemsPerPage.value, totalEmails.value)
    );

    const paginatedEmails = computed(() => emailAddresses.value);

    // Messages pagination computed properties
    const totalMessages = computed(() => totalConversationsCount.value);

    const totalMessagePages = computed(() =>
      Math.max(1, Math.ceil(totalMessages.value / conversationsPerPage.value))
    );

    const messagesStartIndex = computed(
      () => (currentConversationPage.value - 1) * conversationsPerPage.value
    );

    const messagesEndIndex = computed(() =>
      Math.min(
        messagesStartIndex.value + conversationsPerPage.value,
        totalMessages.value
      )
    );

    const paginatedMessages = computed(() => conversations.value);

    const displayedPages = computed(() => {
      const pages = [];

      // Always show first page
      if (totalPages.value > 1) {
        pages.push(1);
      }

      // Add ellipsis if needed
      if (currentPage.value > 3) {
        pages.push("...");
      }

      // Add pages around current page
      for (
        let i = Math.max(2, currentPage.value - 1);
        i <= Math.min(totalPages.value - 1, currentPage.value + 1);
        i++
      ) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (currentPage.value < totalPages.value - 2) {
        pages.push("...");
      }

      // Always show last page if not already added
      if (totalPages.value > 1 && !pages.includes(totalPages.value)) {
        pages.push(totalPages.value);
      }

      return pages;
    });

    const displayedMessagePages = computed(() => {
      const pages = [];

      // Always show first page
      if (totalMessagePages.value > 1) {
        pages.push(1);
      }

      // Add ellipsis if needed
      if (currentConversationPage.value > 3) {
        pages.push("...");
      }

      // Add pages around current page
      for (
        let i = Math.max(2, currentConversationPage.value - 1);
        i <=
        Math.min(
          totalMessagePages.value - 1,
          currentConversationPage.value + 1
        );
        i++
      ) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (currentConversationPage.value < totalMessagePages.value - 2) {
        pages.push("...");
      }

      // Always show last page if not already added
      if (
        totalMessagePages.value > 1 &&
        !pages.includes(totalMessagePages.value)
      ) {
        pages.push(totalMessagePages.value);
      }

      return pages;
    });

    // Watch for search query changes to reset pagination
    watch(searchQuery, () => {
      currentPage.value = 1;
    });

    // Watch for route query changes to handle tab switching
    watch(
      () => route.query.tab,
      (newTab) => {
        if (newTab && tabs.some((tab) => tab.id === newTab)) {
          activeTab.value = newTab;
        }
      },
      { immediate: true }
    );

    // Pagination methods
    const goToPage = (page) => {
      if (typeof page === "number" && page !== currentPage.value) {
        currentPage.value = page;
        if (searchQuery.value) {
          performSearch();
        } else {
          fetchEmailAddresses(page);
        }
      }
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
        if (searchQuery.value) {
          performSearch();
        } else {
          fetchEmailAddresses(currentPage.value);
        }
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        if (searchQuery.value) {
          performSearch();
        } else {
          fetchEmailAddresses(currentPage.value);
        }
      }
    };

    // Search handling
    const performSearch = async () => {
      if (searchQuery.value.length >= 1) {
        try {
          loading.value = true;
          const response = await store.dispatch("searchEmailAddresses", {
            query: searchQuery.value,
            page: currentPage.value,
            page_size: itemsPerPage.value,
          });

          // Update state with server response
          emailAddresses.value = response.results || [];
          totalCount.value = response.count || 0;
          nextPageUrl.value = response.next;
          previousPageUrl.value = response.previous;
        } catch (err) {
          // If server-side search fails, fall back to fetching all data
          console.warn("Server search failed, fetching all data");
          await fetchEmailAddresses(currentPage.value);
        } finally {
          loading.value = false;
        }
      } else {
        // When search is empty, fetch all email addresses
        await fetchEmailAddresses(currentPage.value);
      }
    };

    const clearSearch = () => {
      searchQuery.value = "";
      currentPage.value = 1;
      fetchEmailAddresses(1);
    };

    // Data fetching
    const fetchEmailAddresses = async (page = 1) => {
      try {
        loading.value = true;
        const response = await store.dispatch("fetchEmailAddresses", {
          page,
          page_size: itemsPerPage.value,
        });

        // Update state with server response
        emailAddresses.value = response.results || [];
        totalCount.value = response.count || 0;
        nextPageUrl.value = response.next;
        previousPageUrl.value = response.previous;

        // Update current page if it was passed as parameter
        if (page !== currentPage.value) {
          currentPage.value = page;
        }
      } catch (err) {
        error.value = err.message || "Failed to load email addresses";
      } finally {
        loading.value = false;
      }
    };

    // Navigation
    const goToInbox = (emailId) => {
      router.push({ name: "Inbox", params: { id: emailId } });
    };

    // Delete email
    const deleteEmail = async (email) => {
      if (confirm(`Are you sure you want to delete ${email.email}?`)) {
        try {
          await store.dispatch("deleteEmail", email.id);
          // Refresh the current page after deletion
          if (searchQuery.value) {
            performSearch();
          } else {
            fetchEmailAddresses(currentPage.value);
          }
        } catch (err) {
          error.value = err.message || "Failed to delete email";
        }
      }
    };

    // Format date helper
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("default", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(date);
    };

    // Helper method to get message snippet
    const getMessageSnippet = (content) => {
      if (!content) return "";
      const plainText = content.replace(/<[^>]*>/g, ""); // Remove HTML tags
      return plainText.length > 100
        ? plainText.substring(0, 100) + "..."
        : plainText;
    };

    // Email helper methods
    const getEmailColor = (emailAddress) => {
      if (!emailAddress) return "#3b82f6";
      const colors = [
        "#3b82f6",
        "#8b5cf6",
        "#ec4899",
        "#f97316",
        "#14b8a6",
        "#f59e0b",
        "#10b981",
        "#6366f1",
        "#ef4444",
        "#84cc16",
      ];
      let hash = 0;
      for (let i = 0; i < emailAddress.length; i++) {
        hash = emailAddress.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    };

    const getEmailInitials = (emailAddress) => {
      if (!emailAddress) return "??";
      const localPart = emailAddress.split("@")[0];
      return localPart.slice(0, 2).toUpperCase();
    };

    // Messages handling
    const fetchConversations = async (page = 1) => {
      try {
        conversationsLoading.value = true;
        const response = await store.dispatch("fetchAllConversations", {
          page,
          page_size: conversationsPerPage.value,
        });

        // Update state with server response
        conversations.value = response.results || [];
        totalConversationsCount.value = response.count || 0;
        nextConversationPageUrl.value = response.next;
        previousConversationPageUrl.value = response.previous;

        // Update current page if it was passed as parameter
        if (page !== currentConversationPage.value) {
          currentConversationPage.value = page;
        }
      } catch (err) {
        error.value = err.message || "Failed to load conversations";
      } finally {
        conversationsLoading.value = false;
      }
    };

    const openConversation = (conversationId) => {
      router.push({
        name: "ConversationView",
        params: {
          conversationId: conversationId,
        },
        query: {
          from: "dashboard",
          tab: "conversations",
        },
      });
    };

    const handleDeleteConversation = async (conversationId) => {
      if (confirm("Are you sure you want to delete this conversation?")) {
        try {
          await store.dispatch("deleteConversation", conversationId);
          // Refresh the current page after deletion
          if (conversationSearchQuery.value) {
            performConversationSearch();
          } else {
            fetchConversations(currentConversationPage.value);
          }
        } catch (err) {
          error.value = err.message || "Failed to delete conversation";
        }
      }
    };

    const handleMarkConversationAsRead = async (conversationId) => {
      try {
        await store.dispatch("markConversationAsRead", conversationId);

        // Update the conversation in local state
        const conversationIndex = conversations.value.findIndex(
          (conv) => conv.id === conversationId
        );
        if (conversationIndex !== -1) {
          conversations.value[conversationIndex].unread_count = 0;
        }
      } catch (err) {
        console.error("Failed to mark conversation as read:", err);
        error.value = err.message || "Failed to mark conversation as read";
      }
    };

    // Enhanced search handling
    const handleSearchInput = () => {
      // Hide tips when user starts typing
      if (conversationSearchQuery.value.length > 0) {
        showSearchTips.value = false;
      } else {
        showSearchTips.value = true;
        searchStats.value = null;
      }

      // Clear previous debounce timer
      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value);
      }

      // Auto-search after 500ms of no typing (for queries > 2 chars)
      if (conversationSearchQuery.value.length > 2) {
        searchDebounceTimer.value = setTimeout(() => {
          performConversationSearch();
        }, 500);
      }
    };

    // Enhanced conversation search
    const performConversationSearch = async () => {
      const startTime = Date.now();

      if (conversationSearchQuery.value.length >= 1) {
        try {
          conversationsLoading.value = true;
          const response = await store.dispatch("searchConversations", {
            query: conversationSearchQuery.value,
            page: currentConversationPage.value,
            page_size: conversationsPerPage.value,
          });

          // Update state with server response
          conversations.value = response.results || [];
          totalConversationsCount.value = response.count || 0;
          nextConversationPageUrl.value = response.next;
          previousConversationPageUrl.value = response.previous;

          // Update search stats
          const endTime = Date.now();
          searchStats.value = {
            count: response.count || 0,
            searchTime: endTime - startTime,
          };
        } catch (err) {
          console.error("Search failed:", err);
          error.value = "Search failed. Please try again.";
          // If server-side search fails, fall back to fetching all data
          await fetchConversations(currentConversationPage.value);
          searchStats.value = null;
        } finally {
          conversationsLoading.value = false;
        }
      } else {
        // When search is empty, fetch all conversations
        searchStats.value = null;
        await fetchConversations(currentConversationPage.value);
      }
    };

    const clearConversationSearch = () => {
      conversationSearchQuery.value = "";
      currentConversationPage.value = 1;
      showSearchTips.value = true;
      searchStats.value = null;

      // Clear any pending debounce timer
      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value);
      }

      fetchConversations(1);
    };

    const goToConversationPage = (page) => {
      if (typeof page === "number" && page !== currentConversationPage.value) {
        currentConversationPage.value = page;
        if (conversationSearchQuery.value) {
          performConversationSearch();
        } else {
          fetchConversations(page);
        }
      }
    };

    const prevConversationPage = () => {
      if (currentConversationPage.value > 1) {
        currentConversationPage.value--;
        if (conversationSearchQuery.value) {
          performConversationSearch();
        } else {
          fetchConversations(currentConversationPage.value);
        }
      }
    };

    const nextConversationPage = () => {
      if (currentConversationPage.value < totalMessagePages.value) {
        currentConversationPage.value++;
        if (conversationSearchQuery.value) {
          performConversationSearch();
        } else {
          fetchConversations(currentConversationPage.value);
        }
      }
    };

    onMounted(() => {
      fetchEmailAddresses(1);
      fetchConversations(1);
    });

    return {
      loading,
      error,
      emailAddresses,
      currentPage,
      itemsPerPage,
      searchQuery,
      filteredEmails,
      totalEmails,
      totalPages,
      startIndex,
      endIndex,
      paginatedEmails,
      displayedPages,
      goToPage,
      nextPage,
      prevPage,
      goToInbox,
      formatDate,
      deleteEmail,
      performSearch,
      clearSearch,
      activeTab,
      tabs,
      conversations,
      conversationsLoading,
      conversationSearchQuery,
      currentConversationPage,
      conversationsPerPage,
      totalConversationsCount,
      filteredMessages,
      totalMessages,
      totalMessagePages,
      messagesStartIndex,
      messagesEndIndex,
      paginatedMessages,
      displayedMessagePages,
      performConversationSearch,
      clearConversationSearch,
      fetchConversations,
      goToConversationPage,
      prevConversationPage,
      nextConversationPage,
      getMessageSnippet,
      openConversation,
      handleDeleteConversation,
      handleMarkConversationAsRead,
      handleSearchInput,
      showSearchTips,
      searchStats,
      getEmailColor,
      getEmailInitials,
    };
  },
};
</script>

<style lang="scss">
// Variables
$primary-color: #3b82f6;
$primary-hover: #2563eb;
$primary-light: #eff6ff;
$success-color: #10b981;
$success-light: #d1fae5;
$error-color: #ef4444;
$error-light: #fee2e2;

$text-primary: #111827;
$text-secondary: #4b5563;
$text-muted: #6b7280;
$text-light: #9ca3af;

$border-color: #e5e7eb;
$border-focus: #93c5fd;

$bg-white: #ffffff;
$bg-light: #f9fafb;
$bg-hover: #f3f4f6;

$shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
$shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
$shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
  0 2px 4px -1px rgba(0, 0, 0, 0.06);
$shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
  0 4px 6px -2px rgba(0, 0, 0, 0.05);

$radius-sm: 0.25rem;
$radius: 0.375rem;
$radius-md: 0.5rem;
$radius-lg: 0.75rem;
$radius-full: 9999px;

$transition: all 0.2s ease;

*,
*::before,
*::after {
  box-sizing: border-box;
}

// Base container
.dashboard-container {
  width: 100%;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 1rem;
  color: $text-primary;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

// Loading state
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  text-align: center;

  .spinner {
    width: 3rem;
    height: 3rem;
    margin-bottom: 1rem;
    animation: rotate 2s linear infinite;

    .path {
      stroke: $primary-color;
      stroke-linecap: round;
      animation: dash 1.5s ease-in-out infinite;
    }
  }

  p {
    color: $text-secondary;
    font-size: 1rem;
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

// Error container
.error-container {
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: $error-light;
  border-radius: $radius;
  margin-bottom: 1.5rem;

  .error-icon {
    flex-shrink: 0;
    width: 1.5rem;
    height: 1.5rem;
    margin-right: 0.75rem;
    color: $error-color;
  }

  p {
    margin: 0;
    color: darken($error-color, 15%);
    font-size: 0.875rem;
  }
}

// Dashboard content
.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
}

// Search bar
.search-bar {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.5rem;

  .search-input-wrapper {
    position: relative;
    flex: 1;

    .search-icon {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%);
      width: 1rem;
      height: 1rem;
      color: $text-light;
    }

    .search-input {
      width: 100%;
      height: 2.75rem;
      padding: 0 2.5rem;
      font-size: 0.875rem;
      color: $text-primary;
      background-color: $bg-white;
      border: 1px solid $border-color;
      border-radius: $radius;
      box-shadow: $shadow-sm;
      transition: $transition;

      &::placeholder {
        color: $text-light;
      }

      &:focus {
        outline: none;
        border-color: $border-focus;
        box-shadow: 0 0 0 3px rgba($primary-color, 0.15);
      }
    }

    .clear-button {
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      padding: 0;
      background: none;
      border: none;
      border-radius: $radius-full;
      color: $text-light;
      cursor: pointer;
      transition: $transition;

      svg {
        width: 1rem;
        height: 1rem;
      }

      &:hover {
        background-color: $bg-hover;
        color: $text-primary;
      }
    }
  }

  .search-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 2.75rem;
    padding: 0 1.25rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: white;
    background-color: $primary-color;
    border: none;
    border-radius: $radius;
    cursor: pointer;
    transition: $transition;
    white-space: nowrap;

    &:hover {
      background-color: $primary-hover;
    }
  }
}

// Table styling
.table-container {
  background-color: $bg-white;
  border-radius: $radius-lg;
  box-shadow: $shadow-md;
  overflow: hidden;
  border: 1px solid $border-color;
}

.table-scroll-wrapper {
  overflow-x: auto;
  overflow-y: visible;

  // Hide scrollbar on webkit browsers
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: $bg-light;
  }

  &::-webkit-scrollbar-thumb {
    background: $border-color;
    border-radius: $radius-sm;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: $text-light;
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  // Column widths for better space efficiency
  .col-email {
    width: 30%;
    min-width: 200px;
  }
  .col-subject {
    width: 35%;
    min-width: 250px;
  }
  .col-sender {
    width: 18%;
    min-width: 150px;
  }
  .col-recipient {
    width: 18%;
    min-width: 150px;
  }
  .col-status {
    width: 12%;
    min-width: 100px;
  }
  .col-date {
    width: 12%;
    min-width: 100px;
  }
  .col-actions {
    width: 15%;
    min-width: 140px;
  }

  thead {
    background: linear-gradient(
      135deg,
      $bg-light 0%,
      darken($bg-light, 3%) 100%
    );
    position: sticky;
    top: 0;
    z-index: 10;

    th {
      padding: 1rem 0.75rem;
      text-align: left;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: $text-secondary;
      border-bottom: 2px solid $border-color;
      white-space: nowrap;

      &:first-child {
        padding-left: 1.25rem;
      }

      &:last-child {
        padding-right: 1.25rem;
      }
    }
  }

  tbody {
    .table-row {
      border-bottom: 1px solid rgba($border-color, 0.5);
      transition: all 0.15s ease;

      &:hover {
        background-color: rgba($primary-color, 0.02);
        border-bottom-color: rgba($primary-color, 0.2);
      }

      &.row-unread {
        background-color: rgba($primary-color, 0.04);
        font-weight: 500;

        &:hover {
          background-color: rgba($primary-color, 0.06);
        }
      }

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 0.875rem 0.75rem;
      vertical-align: middle;

      &:first-child {
        padding-left: 1.25rem;
      }

      &:last-child {
        padding-right: 1.25rem;
      }
    }
  }
}

// Email info styling
.email-info {
  .email-address {
    font-weight: 500;
    color: $text-primary;
    margin-bottom: 0.125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      color: $primary-color;
      cursor: pointer;
    }
  }

  .email-meta {
    font-size: 0.75rem;
    color: $text-muted;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// Message info styling
.message-info {
  .message-subject {
    font-weight: 500;
    color: $text-primary;
    margin-bottom: 0.125rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    overflow: hidden;

    .conversation-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.25rem;
      height: 1.25rem;
      padding: 0 0.25rem;
      font-size: 0.625rem;
      font-weight: 600;
      background-color: $primary-color;
      color: white;
      border-radius: $radius-full;
      flex-shrink: 0;
    }
  }

  .message-snippet {
    font-size: 0.75rem;
    color: $text-muted;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
  }
}

// Sender/recipient text
.sender-text,
.recipient-text {
  color: $text-secondary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

// Status indicator
.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  border-radius: $radius-full;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;

  .status-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: $radius-full;
    flex-shrink: 0;
  }

  &.status-active {
    background-color: rgba($success-color, 0.15);
    color: darken($success-color, 15%);

    .status-dot {
      background-color: $success-color;
    }
  }

  &.status-inactive {
    background-color: rgba($error-color, 0.15);
    color: darken($error-color, 15%);

    .status-dot {
      background-color: $error-color;
    }
  }

  &.status-unread {
    background-color: rgba($primary-color, 0.15);
    color: darken($primary-color, 15%);

    .status-dot {
      background-color: $primary-color;
    }
  }

  &.status-read {
    background-color: rgba($success-color, 0.15);
    color: darken($success-color, 15%);

    .status-dot {
      background-color: $success-color;
    }
  }
}

// Message status with star
.message-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .star-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    color: #f59e0b;
    flex-shrink: 0;

    svg {
      width: 0.875rem;
      height: 0.875rem;
    }
  }
}

// Date text
.date-text {
  color: $text-secondary;
  font-size: 0.8125rem;
  white-space: nowrap;
}

// Action buttons
.action-buttons {
  display: flex;
  gap: 0.375rem;
  justify-content: flex-end;
  align-items: center;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4375rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid transparent;
  border-radius: $radius;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  text-decoration: none;

  .icon {
    width: 0.875rem;
    height: 0.875rem;
    stroke-width: 2;
    flex-shrink: 0;
  }

  .btn-text {
    display: none;

    @media (min-width: 1024px) {
      display: inline;
    }
  }

  &.btn-primary {
    color: white;
    background-color: $primary-color;
    border-color: $primary-color;

    &:hover {
      background-color: $primary-hover;
      border-color: $primary-hover;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba($primary-color, 0.2);
    }
  }

  &.btn-secondary {
    color: $text-secondary;
    background-color: $bg-white;
    border-color: $border-color;

    &:hover {
      background-color: $bg-light;
      border-color: darken($border-color, 10%);
      color: $text-primary;
    }
  }

  &.btn-danger {
    color: $error-color;
    background-color: rgba($error-color, 0.05);
    border-color: rgba($error-color, 0.2);

    &:hover {
      background-color: rgba($error-color, 0.1);
      border-color: rgba($error-color, 0.4);
      color: darken($error-color, 10%);
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .data-table {
    font-size: 0.8125rem;

    .col-email {
      min-width: 180px;
    }
    .col-subject {
      min-width: 200px;
    }
    .col-sender {
      min-width: 120px;
    }
    .col-recipient {
      min-width: 120px;
    }
    .col-status {
      min-width: 80px;
    }
    .col-date {
      min-width: 80px;
    }
    .col-actions {
      min-width: 100px;
    }

    thead th {
      padding: 0.75rem 0.5rem;
      font-size: 0.7rem;
    }

    tbody td {
      padding: 0.75rem 0.5rem;
    }
  }

  .btn-action {
    padding: 0.375rem 0.5rem;

    .btn-text {
      display: none !important;
    }
  }
}

// Empty state
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-md;

  .empty-state-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4rem;
    height: 4rem;
    margin-bottom: 1.5rem;
    background-color: $primary-light;
    border-radius: $radius-full;
    color: $primary-color;

    svg {
      width: 2rem;
      height: 2rem;
      stroke-width: 1.5;
    }
  }

  .empty-state-title {
    margin: 0 0 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: $text-primary;
  }

  .empty-state-message {
    margin: 0;
    font-size: 0.875rem;
    color: $text-secondary;
    max-width: 24rem;
  }
}

// Pagination
.pagination-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-md;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.pagination-info {
  font-size: 0.875rem;
  color: $text-secondary;

  .highlight {
    font-weight: 500;
    color: $text-primary;
  }
}

.pagination-controls {
  display: none;
  align-items: center;
  gap: 0.25rem;

  @media (min-width: 768px) {
    display: flex;
  }
}

.pagination-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  background-color: $bg-white;
  border: 1px solid $border-color;
  border-radius: $radius;
  cursor: pointer;
  transition: $transition;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: $text-secondary;
  }

  &:hover:not(:disabled) {
    background-color: $bg-hover;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.pagination-pages {
  display: flex;
  gap: 0.25rem;
}

.pagination-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 0.5rem;
  font-size: 0.875rem;
  color: $text-secondary;
  background-color: $bg-white;
  border: 1px solid $border-color;
  border-radius: $radius;
  cursor: pointer;
  transition: $transition;

  &:hover:not(.active):not(.ellipsis) {
    background-color: $bg-hover;
  }

  &.active {
    color: white;
    background-color: $primary-color;
    border-color: $primary-color;
  }

  &.ellipsis {
    cursor: default;
  }
}

.mobile-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media (min-width: 768px) {
    display: none;
  }

  .mobile-pagination-button {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: $text-secondary;
    background-color: $bg-white;
    border: 1px solid $border-color;
    border-radius: $radius;
    cursor: pointer;
    transition: $transition;

    &:hover:not(:disabled) {
      background-color: $bg-hover;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .mobile-pagination-info {
    font-size: 0.875rem;
    color: $text-secondary;
  }
}

// Screen reader only
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.email-accounts-section,
.conversations-section {
  background: $bg-white;
  border-radius: $radius-lg;
  padding: 2rem;
  box-shadow: $shadow;
}

.tab-navigation {
  background: $bg-white;
  border-radius: $radius-lg;
  padding: 0.5rem;
  box-shadow: $shadow;
  margin-bottom: 0.5rem;

  .tab-list {
    display: flex;
    gap: 0.25rem;
  }

  .tab-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: $text-secondary;
    background-color: transparent;
    border: none;
    border-radius: $radius-md;
    cursor: pointer;
    transition: $transition;
    position: relative;
    white-space: nowrap;

    .tab-icon {
      width: 1.125rem;
      height: 1.125rem;
      stroke-width: 2;
    }

    &:hover:not(.active) {
      background-color: $bg-hover;
      color: $text-primary;
    }

    &.active {
      color: $primary-color;
      background-color: $primary-light;

      .tab-icon {
        color: $primary-color;
      }
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba($primary-color, 0.2);
    }
  }
}

.tab-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tab-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Conversation list container
.conversation-list-container {
  width: 100%;
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-md;
  overflow: visible;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

// Enhanced search section styles
.search-section {
  .search-bar {
    .search-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .button-spinner {
        width: 1rem;
        height: 1rem;
        animation: spin 1s linear infinite;
      }
    }
  }

  .search-tips {
    margin-top: 1rem;
    padding: 0.875rem 1rem;
    background: linear-gradient(
      135deg,
      rgba($primary-color, 0.05),
      rgba($primary-color, 0.02)
    );
    border: 1px solid rgba($primary-color, 0.15);
    border-radius: $radius-md;

    .tips-title {
      font-size: 0.8rem;
      font-weight: 600;
      color: $primary-color;
      margin: 0 0 0.5rem 0;
    }

    .tips-list {
      margin: 0;
      padding-left: 1.25rem;
      font-size: 0.75rem;
      color: $text-secondary;

      li {
        margin-bottom: 0.25rem;
        line-height: 1.4;

        &:last-child {
          margin-bottom: 0;
        }

        strong {
          color: $text-primary;
          font-weight: 600;
        }
      }
    }
  }

  .search-stats {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.5rem 0.75rem;
    background: rgba($success-color, 0.05);
    border: 1px solid rgba($success-color, 0.2);
    border-radius: $radius;

    .stats-icon {
      width: 1rem;
      height: 1rem;
      color: $success-color;
      flex-shrink: 0;
    }

    .stats-text {
      font-size: 0.75rem;
      color: darken($success-color, 10%);
      font-weight: 500;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Email list container styles
.email-list-container {
  width: 100%;
  background: $bg-white;
  border-radius: $radius-lg;
  overflow: hidden;
  box-shadow: $shadow;

  .list-header {
    background: linear-gradient(135deg, $bg-light, $bg-white);
    border-bottom: 2px solid $border-color;
    padding: 0.75rem;

    .header-row {
      display: grid;
      grid-template-columns: 1fr 120px 100px 80px;
      gap: 0.75rem;
      align-items: center;

      @media (max-width: 768px) {
        grid-template-columns: 1fr 100px 60px;
        .date {
          display: none;
        }
      }

      .column-header {
        font-size: 0.75rem;
        font-weight: 700;
        color: $text-secondary;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    }
  }

  .list-body {
    .email-row {
      display: grid;
      grid-template-columns: 1fr 120px 100px 80px;
      gap: 0.75rem;
      align-items: center;
      padding: 0.875rem 0.75rem;
      border-bottom: 1px solid $border-color;
      cursor: pointer;
      transition: $transition;

      @media (max-width: 768px) {
        grid-template-columns: 1fr 100px 60px;
        .date {
          display: none;
        }
      }

      &:hover {
        background-color: $bg-hover;
      }

      &:last-child {
        border-bottom: none;
      }

      .column-content {
        min-width: 0;
      }

      // Email column
      .email {
        .email-info {
          .email-address {
            display: flex;
            align-items: center;
            gap: 0.75rem;

            .email-avatar {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 2.25rem;
              height: 2.25rem;
              border-radius: 50%;
              color: white;
              font-size: 0.75rem;
              font-weight: 700;
              flex-shrink: 0;
            }

            .email-details {
              flex: 1;
              min-width: 0;

              .email-text {
                font-size: 0.875rem;
                font-weight: 500;
                color: $text-primary;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                margin-bottom: 0.125rem;
              }

              .email-meta {
                font-size: 0.75rem;
                color: $text-muted;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
        }
      }

      // Status column
      .status {
        .status-indicator {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.25rem 0.5rem;
          border-radius: $radius-full;
          font-size: 0.75rem;
          font-weight: 500;
          white-space: nowrap;

          .status-dot {
            width: 0.5rem;
            height: 0.5rem;
            border-radius: $radius-full;
            flex-shrink: 0;
          }

          &.status-active {
            background-color: rgba($success-color, 0.15);
            color: darken($success-color, 15%);

            .status-dot {
              background-color: $success-color;
            }
          }

          &.status-inactive {
            background-color: rgba($error-color, 0.15);
            color: darken($error-color, 15%);

            .status-dot {
              background-color: $error-color;
            }
          }
        }
      }

      // Date column
      .date {
        .date-info {
          .date-text {
            font-size: 0.75rem;
            font-weight: 500;
            color: $text-secondary;
            white-space: nowrap;
          }
        }
      }

      // Actions column
      .actions {
        .action-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
          opacity: 0.7;
          transition: $transition;

          .action-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;
            background: transparent;
            border: 1px solid $border-color;
            border-radius: $radius;
            color: $text-muted;
            cursor: pointer;
            transition: $transition;
            padding: 0;

            &:hover {
              background: $bg-light;
              border-color: $primary-color;
              color: $primary-color;
              transform: scale(1.05);
            }

            &.delete-button:hover {
              background: rgba($error-color, 0.1);
              border-color: $error-color;
              color: $error-color;
            }

            svg {
              width: 1rem;
              height: 1rem;
              stroke-width: 2;
            }
          }
        }
      }

      &:hover .action-buttons {
        opacity: 1;
      }
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
